import { useCallback, useContext, useRef, useState } from 'react';

import { WebSocketContext } from '../../websocket-context/websocket-provider';
import {
  BACKTESTER_CONFIG_KEYS,
  buildDeleteConfigRequest,
  buildFetchConfigRequest,
  buildListConfigsRequest,
  buildSaveConfigRequest,
  parseBacktesterConfigListResult,
  parseFetchBacktesterConfigResult,
} from '../api/backtester-config-api';
import {
  BacktesterConfigSummary,
  BacktesterParamsPayload,
  DeleteBacktesterConfigResponse,
  FetchBacktesterConfigResponse,
  FetchBacktesterConfigResult,
  ListBacktesterConfigsResponse,
  SaveBacktesterConfigResponse,
} from '../api/backtester-config-api.types';

const DEFAULT_PAGE_SIZE = 10;

interface OperationCallbacks {
  onSuccess?: () => void;
  onError?: () => void;
}

export const useBacktesterConfigs = () => {
  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const latestFetchRequestNameRef = useRef<string | null>(null);

  const [isListing, setIsListing] = useState(false);
  const [configs, setConfigs] = useState<BacktesterConfigSummary[] | null>(null);

  const [isFetching, setIsFetching] = useState(false);
  const [fetchedConfig, setFetchedConfig] = useState<FetchBacktesterConfigResult | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [savedConfigName, setSavedConfigName] = useState<string | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [deletedConfigName, setDeletedConfigName] = useState<string | null>(null);

  const listConfigs = useCallback(
    (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      setIsListing(true);

      const req = buildListConfigsRequest({ page, page_size: pageSize });
      sendMessage(JSON.stringify(req));

      subscribeToChannel(BACKTESTER_CONFIG_KEYS.LIST, (msg: ListBacktesterConfigsResponse) => {
        setIsListing(false);
        setConfigs(parseBacktesterConfigListResult(msg.result));
      });
    },
    [sendMessage, subscribeToChannel],
  );

  const fetchConfig = useCallback(
    (name: string) => {
      latestFetchRequestNameRef.current = name;
      setIsFetching(true);

      const req = buildFetchConfigRequest({ name });
      sendMessage(JSON.stringify(req));

      subscribeToChannel(BACKTESTER_CONFIG_KEYS.FETCH, (msg: FetchBacktesterConfigResponse) => {
        const parsed = parseFetchBacktesterConfigResult(msg.result);
        if (!parsed) {
          setIsFetching(false);
          return;
        }

        if (latestFetchRequestNameRef.current !== parsed.name) return;

        setIsFetching(false);
        setFetchedConfig(parsed);
      });
    },
    [sendMessage, subscribeToChannel],
  );

  const saveConfig = useCallback(
    (name: string, config: BacktesterParamsPayload, callbacks?: OperationCallbacks) => {
      setIsSaving(true);

      const req = buildSaveConfigRequest({ name, config });
      sendMessage(JSON.stringify(req));

      subscribeToChannel(BACKTESTER_CONFIG_KEYS.SAVE, (msg: SaveBacktesterConfigResponse) => {
        setIsSaving(false);
        if (msg.result === 'success' && !msg.error) {
          setSavedConfigName(name);
          callbacks?.onSuccess?.();
        } else {
          callbacks?.onError?.();
        }
      });
    },
    [sendMessage, subscribeToChannel],
  );

  const deleteConfig = useCallback(
    (name: string, callbacks?: OperationCallbacks) => {
      setIsDeleting(true);

      const req = buildDeleteConfigRequest({ name });
      sendMessage(JSON.stringify(req));

      subscribeToChannel(BACKTESTER_CONFIG_KEYS.DELETE, (msg: DeleteBacktesterConfigResponse) => {
        setIsDeleting(false);
        if (msg.result === 'success' && !msg.error) {
          setDeletedConfigName(name);
          callbacks?.onSuccess?.();
        } else {
          callbacks?.onError?.();
        }
      });
    },
    [sendMessage, subscribeToChannel],
  );

  return {
    listConfigs,
    fetchConfig,
    saveConfig,
    deleteConfig,
    isListing,
    configs,
    isFetching,
    fetchedConfig,
    isSaving,
    savedConfigName,
    isDeleting,
    deletedConfigName,
  };
};
